import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getUserDirectory } from '../../../api/v2/super_users/users';
import { roleClassFor, roleLabelFor } from '../roles';

const USER_GROUPS = [
  { profileType: 'AccountAdmin', label: 'Account admins' },
  { profileType: 'ClinicalUser', label: 'Clinical users' },
  { profileType: 'PatientPartner', label: 'Patient partners' },
  { profileType: 'Patient', label: 'Patients' },
  { profileType: null, label: 'Other users' },
];

const groupUsers = (users) => {
  return USER_GROUPS.map((group) => {
    const groupedUsers = users
      .filter((user) =>
        group.profileType
          ? user.current_profile_type === group.profileType
          : !USER_GROUPS.some(
              (knownGroup) => knownGroup.profileType && knownGroup.profileType === user.current_profile_type
            )
      )
      .sort((first, second) => (first.name || first.email).localeCompare(second.name || second.email));

    return { ...group, users: groupedUsers };
  }).filter((group) => group.users.length > 0);
};

const GlobalUserSwitcher = ({ currentUserId, onSelect }) => {
  const [accounts, setAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [openAccount, setOpenAccount] = useState(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const requestId = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(
      () => {
        const currentRequest = requestId.current + 1;
        requestId.current = currentRequest;
        setLoading(true);
        setError('');

        getUserDirectory({
          accountUuid: openAccount?.uuid,
          query: openAccount ? undefined : query.trim(),
        })
          .then((data) => {
            if (requestId.current !== currentRequest) return;
            setAccounts(data.accounts);
            setUsers(data.users);
          })
          .catch(() => {
            if (requestId.current !== currentRequest) return;
            setError('Could not load users. Please try again.');
          })
          .finally(() => {
            if (requestId.current === currentRequest) setLoading(false);
          });
      },
      query ? 180 : 0
    );

    return () => window.clearTimeout(timer);
  }, [openAccount, query]);

  const visibleAccounts = useMemo(() => {
    if (openAccount) return [openAccount];
    if (!query.trim()) return accounts;

    const term = query.trim().toLowerCase();
    const matchingAccountUuids = new Set(users.map((user) => user.account_uuid));
    return accounts.filter(
      (account) => account.name.toLowerCase().includes(term) || matchingAccountUuids.has(account.uuid)
    );
  }, [accounts, openAccount, query, users]);

  const usersByAccount = useMemo(() => {
    const term = query.trim().toLowerCase();
    const visibleUsers =
      openAccount && term
        ? users.filter(
            (user) => (user.name || '').toLowerCase().includes(term) || (user.email || '').toLowerCase().includes(term)
          )
        : users;

    return visibleUsers.reduce((grouped, user) => {
      if (!grouped[user.account_uuid]) {
        grouped[user.account_uuid] = [];
      }
      grouped[user.account_uuid].push(user);
      return grouped;
    }, {});
  }, [openAccount, query, users]);

  const open = (account) => {
    setOpenAccount(account);
    setUsers([]);
    setQuery('');
  };

  const close = () => {
    setOpenAccount(null);
    setUsers([]);
    setQuery('');
  };

  return (
    <div className='pp-super-user-directory' data-testid='global-user-switcher'>
      <div className='pp-super-user-directory-search-row'>
        {openAccount && (
          <button type='button' className='pp-super-user-directory-back' onClick={close} aria-label='Back to accounts'>
            ‹
          </button>
        )}
        <label className='pp-super-user-directory-search'>
          <span className='pp-super-user-directory-search-icon' aria-hidden='true'>
            ⌕
          </span>
          <input
            type='search'
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={openAccount ? `Filter ${openAccount.name} users` : 'Search account name or user email'}
            aria-label='Search users'
            autoFocus
          />
        </label>
      </div>

      <div className='pp-super-user-directory-results'>
        {Boolean(error) && <p className='pp-super-user-directory-message'>{error}</p>}
        {!error && loading && <p className='pp-super-user-directory-message'>Loading…</p>}
        {!error && !loading && visibleAccounts.length === 0 && (
          <p className='pp-super-user-directory-message'>No users found.</p>
        )}

        {!error &&
          visibleAccounts.map((account) => {
            const accountUsers = usersByAccount[account.uuid] || [];
            const userGroups = groupUsers(accountUsers);
            const expanded = openAccount?.uuid === account.uuid || Boolean(query.trim() && accountUsers.length);
            const accountType = (account.account_type || '').replaceAll('_', ' ');
            const accountSummary = `${accountType} · ${account.status || ''}`;

            return (
              <section className='pp-super-user-directory-account' key={account.uuid}>
                <button
                  type='button'
                  className='pp-super-user-directory-account-button'
                  onClick={() => (openAccount?.uuid === account.uuid ? close() : open(account))}
                  aria-expanded={expanded}
                >
                  <span className='pp-super-user-directory-mark'>{account.name.slice(0, 2).toUpperCase()}</span>
                  <span className='pp-super-user-directory-account-details'>
                    <strong>{account.name}</strong>
                    <small>{accountSummary}</small>
                  </span>
                  <span className='pp-super-user-directory-caret' aria-hidden='true'>
                    {expanded ? '⌄' : '›'}
                  </span>
                </button>

                {expanded && (
                  <div className='pp-super-user-directory-users'>
                    {!loading && accountUsers.length === 0 && (
                      <p className='pp-super-user-directory-message'>No matching users in this account.</p>
                    )}
                    {userGroups.map((group) => (
                      <div className='pp-super-user-directory-group' key={group.label}>
                        <p className='pp-super-user-directory-group-label'>{group.label}</p>
                        {group.users.map((user) => (
                          <button
                            type='button'
                            className={`pp-super-user-directory-user ${roleClassFor(user.current_profile_type)}`}
                            key={user.id}
                            onClick={() => onSelect(user)}
                            disabled={user.id === currentUserId}
                          >
                            <span className='pp-super-user-directory-avatar'>
                              {(user.name || user.email).slice(0, 1).toUpperCase()}
                            </span>
                            <span className='pp-super-user-directory-user-details'>
                              <span className='pp-super-user-directory-user-name-row'>
                                <strong>{user.name || user.email}</strong>
                                <span className='pp-super-user-directory-role'>
                                  {roleLabelFor(user.current_profile_type)}
                                </span>
                              </span>
                              <small>{user.email}</small>
                            </span>
                            {(user.conversations_count != null ||
                              (user.current_profile_type === 'Patient' && user.questionnaire_finished != null)) && (
                              <span className='pp-super-user-directory-user-signals'>
                                {user.conversations_count != null && (
                                  <span
                                    className='pp-super-user-directory-conversations'
                                    title={`${user.conversations_count} conversation${
                                      user.conversations_count === 1 ? '' : 's'
                                    }`}
                                  >
                                    {user.conversations_count}
                                  </span>
                                )}
                                {user.current_profile_type === 'Patient' && user.questionnaire_finished != null && (
                                  <span
                                    className={`pp-super-user-directory-questionnaire ${
                                      user.questionnaire_finished ? 'is-complete' : 'is-incomplete'
                                    }`}
                                    title={
                                      user.questionnaire_finished
                                        ? 'Questionnaire complete'
                                        : 'Questionnaire incomplete'
                                    }
                                  />
                                )}
                              </span>
                            )}
                            {user.id === currentUserId && (
                              <span className='pp-super-user-directory-current'>Current</span>
                            )}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
      </div>
    </div>
  );
};

export default GlobalUserSwitcher;
